import {
  AppBar,
  Avatar,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Brightness4Icon from '@mui/icons-material/Brightness4'
import Brightness7Icon from '@mui/icons-material/Brightness7'
import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, type ReactNode } from 'react'
import { useThemeMode } from '../contexts/ThemeContext'
import { useAppData } from '../contexts/AppDataContext'
import { useOnlineStatus } from '../hooks/useOnlineStatus'
import { useNavItems } from './useNavItems'
import { useAppBase, appPath } from './appBase'
import EventLogo from './EventLogo'
import NavRail from './NavRail'
import RealtimeBanner from './RealtimeBanner'
import ConciergeWidget from './ConciergeWidget'
import ErrorBoundary from './ErrorBoundary'

const RAIL_WIDTH = 248
// Mobile bottom bar: keep 4 direct items + a 5th "More" slot for the rest, so
// nothing is ever hidden as the nav grows.
const MAX_BOTTOM_ITEMS = 4

export default function AppShell({ children }: { children: ReactNode }) {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { mode, toggleTheme } = useThemeMode()
  const online = useOnlineStatus()
  const location = useLocation()
  const navigate = useNavigate()
  const base = useAppBase()
  const { attendee, event } = useAppData()
  const navItems = useNavItems()
  const [moreOpen, setMoreOpen] = useState(false)

  // Mobile bottom nav is space-limited. Show the first few nav items directly,
  // and put everything else (secondary built-ins like Network/Badge/Watch and
  // any admin-created custom pages) behind a "More" sheet so nothing is hidden.
  const barItems = navItems.slice(0, MAX_BOTTOM_ITEMS)
  const overflowItems = navItems.slice(MAX_BOTTOM_ITEMS)
  const hasMore = overflowItems.length > 0

  const matches = (sub: string) => {
    const to = appPath(base, sub)
    return sub === '' ? location.pathname === to : location.pathname.startsWith(to)
  }

  const barActiveIndex = barItems.findIndex((item) => matches(item.sub))
  const onOverflowRoute = overflowItems.some((item) => matches(item.sub))
  // When on an overflow route, highlight the "More" slot (last index).
  const activeIndex = onOverflowRoute && hasMore ? barItems.length : barActiveIndex

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <RealtimeBanner />
      {isDesktop && (
        <Drawer
          variant="permanent"
          sx={{
            width: RAIL_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: RAIL_WIDTH,
              boxSizing: 'border-box',
              border: 'none',
              bgcolor: 'background.paper',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          <NavRail />
        </Drawer>
      )}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar
          position="sticky"
          elevation={0}
          color="inherit"
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Toolbar sx={{ gap: 1 }}>
            {!isDesktop && (
              <Box
                component={Link}
                to={base}
                sx={{ display: 'flex', alignItems: 'center', mr: 1 }}
              >
                <EventLogo />
              </Box>
            )}
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
                flexGrow: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {isDesktop ? '' : event?.name ?? ''}
            </Typography>

            {!online && (
              <Tooltip title="You're offline — showing saved content">
                <Chip
                  size="small"
                  icon={<CloudOffRoundedIcon />}
                  label="Offline"
                  color="warning"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Tooltip>
            )}

            <Tooltip title={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}>
              <IconButton onClick={toggleTheme} color="inherit">
                {mode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
              </IconButton>
            </Tooltip>

            <Tooltip title={attendee ? attendee.name : 'Attendee'}>
              <IconButton onClick={() => navigate(appPath(base, 'profile'))} sx={{ p: 0.5 }}>
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor: 'primary.main',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                  }}
                >
                  {attendee?.initials ?? '···'}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ flex: 1, pb: { xs: 9, md: 0 } }}>
          <ErrorBoundary label="page">{children}</ErrorBoundary>
        </Box>
      </Box>

      {/* AI Concierge — renders only when the event has it enabled (both gates). */}
      <ConciergeWidget />

      {!isDesktop && (
        <BottomNavigation
          value={activeIndex}
          showLabels
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
            borderTop: '1px solid',
            borderColor: 'divider',
            height: 64,
          }}
        >
          {barItems.map((item) => (
            <BottomNavigationAction
              key={item.sub}
              component={Link}
              to={appPath(base, item.sub)}
              label={item.label}
              icon={<item.icon />}
            />
          ))}
          {hasMore && (
            <BottomNavigationAction
              label="More"
              icon={<MoreHorizRoundedIcon />}
              onClick={() => setMoreOpen(true)}
            />
          )}
        </BottomNavigation>
      )}

      {/* Mobile "More" sheet: every nav item that doesn't fit in the bar,
          plus quick access to the profile. */}
      <Drawer
        anchor="bottom"
        open={moreOpen && !isDesktop}
        onClose={() => setMoreOpen(false)}
        PaperProps={{
          sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, pb: 2 },
        }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 2,
              bgcolor: 'divider',
              mx: 'auto',
              mb: 1.5,
            }}
          />
          <Typography variant="overline" color="text.secondary">
            More
          </Typography>
        </Box>
        <List sx={{ pt: 0 }}>
          {overflowItems.map((item) => {
            const to = appPath(base, item.sub)
            const active = matches(item.sub)
            return (
              <ListItemButton
                key={item.sub}
                component={Link}
                to={to}
                selected={active}
                onClick={() => setMoreOpen(false)}
              >
                <ListItemIcon sx={{ color: active ? 'primary.main' : 'text.secondary' }}>
                  <item.icon />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: active ? 700 : 500 }}
                />
              </ListItemButton>
            )
          })}
          {attendee && (
            <>
              <Divider sx={{ my: 1 }} />
              <ListItemButton
                component={Link}
                to={appPath(base, 'profile')}
                selected={matches('profile')}
                onClick={() => setMoreOpen(false)}
              >
                <ListItemIcon
                  sx={{ color: matches('profile') ? 'primary.main' : 'text.secondary' }}
                >
                  <PersonRoundedIcon />
                </ListItemIcon>
                <ListItemText primary="My profile" />
              </ListItemButton>
            </>
          )}
        </List>
      </Drawer>
    </Box>
  )
}
